import Axios from 'api/client';
import {
    Contact,
    ContactDuplicate,
    DataHygieneAddress,
    DataHygieneEmail,
    DataHygieneMetrics,
    DataHygienePhoneNumber,
} from 'types/domain';
import { PaginatedData } from 'types/pagination';
import { FilterParams } from 'types/params';

interface FetchDataHygieneParams extends FilterParams {
    options?: Record<string, any>;
}

const activateDataHygiene = (accountId: number) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/activate`).then(
        response => response.data,
    );
};

const fetchDataHygieneMetrics = (accountId: number) => {
    return Axios.get<DataHygieneMetrics>(`/accounts/${accountId}/data-hygiene/metrics`).then(
        response => response.data,
    );
};

const fetchDataHygieneDuplicates = (accountId: number) =>
    Axios.get<PaginatedData<[ContactDuplicate, ContactDuplicate]>>(
        `/accounts/${accountId}/contacts/duplicates`,
    ).then(response => response.data);

const ignoreDataHygieneDuplicate = (accountId: number, contactId: number) =>
    Axios.post<Contact>(`/accounts/${accountId}/contacts/${contactId}/skip-duplicate`).then(
        response => response.data,
    );

interface FixDataHygieneAddressParams {
    address_1: string;
    address_2: string | null;
    city: string;
    state: string | null;
    country: string;
    zipcode: string | null;
}

interface BulkIgnoreDataHygieneAddressesParams {
    ids: number[];
    row_ids: string[];
}

const fetchDataHygieneAddresses = (accountId: number, params: FetchDataHygieneParams = {}) => {
    const { eager = false, include, filters, options, ...restParams } = params;

    return Axios.post<PaginatedData<DataHygieneAddress>>(
        `/accounts/${accountId}/data-hygiene/addresses`,
        {
            ...filters,
            ...restParams,
            ...options,
            include: (include ?? eager) ? 'contact,address' : '',
        },
    ).then(response => response.data);
};

const fixDataHygieneAddress = (
    accountId: number,
    addressCheckId: number,
    params: FixDataHygieneAddressParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/addresses/${addressCheckId}/fix`, {
        ...params,
    }).then(response => response.data);
};

const ignoreDataHygieneAddress = (accountId: number, addressCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/addresses/${addressCheckId}/ignore`,
    ).then(response => response.data);
};

const bulkIgnoreDataHygieneAddresses = (
    accountId: number,
    params: BulkIgnoreDataHygieneAddressesParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/addresses/batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoBulkIgnoreDataHygieneAddresses = (
    accountId: number,
    params: BulkIgnoreDataHygieneAddressesParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/addresses/undo-batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoDataHygieneAddress = (accountId: number, addressCheckId: number) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/addresses/${addressCheckId}/undo`).then(
        response => response.data,
    );
};

const saveForLaterDataHygieneAddress = (accountId: number, addressCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/addresses/${addressCheckId}/save-for-later`,
    ).then(response => response.data);
};

const undoSaveForLaterDataHygieneAddress = (accountId: number, addressCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/addresses/${addressCheckId}/undo-save-for-later`,
    ).then(response => response.data);
};

const dismissAutoFormattedAddresses = (accountId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/metrics/dismiss-auto-formatted-addresses`,
    ).then(response => response.data);
};

interface FixDataHygieneEmailParams {
    email: string;
}

interface BulkIgnoreDataHygieneEmailsParams {
    ids: number[];
    row_ids: string[];
}

const fetchDataHygieneEmails = (accountId: number, params: FetchDataHygieneParams = {}) => {
    const { eager = false, include, filters, options, ...restParams } = params;

    return Axios.post<PaginatedData<DataHygieneEmail>>(
        `/accounts/${accountId}/data-hygiene/emails`,
        {
            ...filters,
            ...restParams,
            ...options,
            include: (include ?? eager) ? 'contact' : '',
        },
    ).then(response => response.data);
};

const fixDataHygieneEmail = (
    accountId: number,
    emailCheckId: number,
    params: FixDataHygieneEmailParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/emails/${emailCheckId}/fix`, {
        ...params,
    }).then(response => response.data);
};

const ignoreDataHygieneEmail = (accountId: number, emailCheckId: number) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/emails/${emailCheckId}/ignore`).then(
        response => response.data,
    );
};

const bulkIgnoreDataHygieneEmails = (
    accountId: number,
    params: BulkIgnoreDataHygieneEmailsParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/emails/batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoBulkIgnoreDataHygieneEmails = (
    accountId: number,
    params: BulkIgnoreDataHygieneEmailsParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/emails/undo-batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoDataHygieneEmail = (accountId: number, emailCheckId: number) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/emails/${emailCheckId}/undo`).then(
        response => response.data,
    );
};

const saveForLaterDataHygieneEmail = (accountId: number, emailCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/emails/${emailCheckId}/save-for-later`,
    ).then(response => response.data);
};

const undoSaveForLaterDataHygieneEmail = (accountId: number, emailCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/emails/${emailCheckId}/undo-save-for-later`,
    ).then(response => response.data);
};

interface FixDataHygienePhoneNumberParams {
    phone_number: string;
}

interface BulkIgnoreDataHygienePhoneNumbersParams {
    ids: number[];
    row_ids: string[];
}

const fetchDataHygienePhoneNumbers = (accountId: number, params: FetchDataHygieneParams = {}) => {
    const { eager = false, include, filters, options, ...restParams } = params;

    return Axios.post<PaginatedData<DataHygienePhoneNumber>>(
        `/accounts/${accountId}/data-hygiene/phone-numbers`,
        {
            ...filters,
            ...restParams,
            ...options,
            include: (include ?? eager) ? 'contact' : '',
        },
    ).then(response => response.data);
};

const fixDataHygienePhoneNumber = (
    accountId: number,
    phoneNumberCheckId: number,
    params: FixDataHygienePhoneNumberParams,
) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/phone-numbers/${phoneNumberCheckId}/fix`,
        {
            ...params,
        },
    ).then(response => response.data);
};

const ignoreDataHygienePhoneNumber = (accountId: number, phoneNumberCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/phone-numbers/${phoneNumberCheckId}/ignore`,
    ).then(response => response.data);
};

const bulkIgnoreDataHygienePhoneNumbers = (
    accountId: number,
    params: BulkIgnoreDataHygienePhoneNumbersParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/phone-numbers/batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoBulkIgnoreDataHygienePhoneNumbers = (
    accountId: number,
    params: BulkIgnoreDataHygienePhoneNumbersParams,
) => {
    return Axios.post(`/accounts/${accountId}/data-hygiene/phone-numbers/undo-batch-ignore`, {
        ...params,
    }).then(response => response.data);
};

const undoDataHygienePhoneNumber = (accountId: number, phoneNumberCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/phone-numbers/${phoneNumberCheckId}/undo`,
    ).then(response => response.data);
};

const saveForLaterDataHygienePhoneNumber = (accountId: number, phoneNumberCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/phone-numbers/${phoneNumberCheckId}/save-for-later`,
    ).then(response => response.data);
};

const undoSaveForLaterDataHygienePhoneNumber = (accountId: number, phoneNumberCheckId: number) => {
    return Axios.post(
        `/accounts/${accountId}/data-hygiene/phone-numbers/${phoneNumberCheckId}/undo-save-for-later`,
    ).then(response => response.data);
};

export {
    activateDataHygiene,
    //
    fetchDataHygieneMetrics,
    //
    fetchDataHygieneDuplicates,
    ignoreDataHygieneDuplicate,
    //
    fetchDataHygieneAddresses,
    fixDataHygieneAddress,
    ignoreDataHygieneAddress,
    bulkIgnoreDataHygieneAddresses,
    undoBulkIgnoreDataHygieneAddresses,
    undoDataHygieneAddress,
    saveForLaterDataHygieneAddress,
    undoSaveForLaterDataHygieneAddress,
    dismissAutoFormattedAddresses,
    //
    fetchDataHygieneEmails,
    fixDataHygieneEmail,
    ignoreDataHygieneEmail,
    bulkIgnoreDataHygieneEmails,
    undoBulkIgnoreDataHygieneEmails,
    undoDataHygieneEmail,
    saveForLaterDataHygieneEmail,
    undoSaveForLaterDataHygieneEmail,
    //
    fetchDataHygienePhoneNumbers,
    fixDataHygienePhoneNumber,
    ignoreDataHygienePhoneNumber,
    bulkIgnoreDataHygienePhoneNumbers,
    undoBulkIgnoreDataHygienePhoneNumbers,
    undoDataHygienePhoneNumber,
    saveForLaterDataHygienePhoneNumber,
    undoSaveForLaterDataHygienePhoneNumber,
};
export type {
    FetchDataHygieneParams,
    FixDataHygieneAddressParams,
    BulkIgnoreDataHygieneAddressesParams,
    FixDataHygieneEmailParams,
    BulkIgnoreDataHygieneEmailsParams,
    FixDataHygienePhoneNumberParams,
    BulkIgnoreDataHygienePhoneNumbersParams,
};
